import urls from "../../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../../utils/service";

export async function getPaymentList(payload: {
  pageNo?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  /** "test" = Easebuzz sandbox attempts, "prod" = live money, "" = both */
  env?: string;
  organizationId?: string;
}) {
  const page = payload.pageNo ?? 0;
  const limit = payload.pageSize ?? 25;
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search: payload.search || "",
  });
  if (payload.status && payload.status !== "all") {
    params.set("status", payload.status);
  }
  if (payload.env && payload.env !== "all") {
    params.set("env", payload.env);
  }
  if (
    payload.organizationId &&
    payload.organizationId !== "all" &&
    payload.organizationId !== "*"
  ) {
    params.set("organizationId", payload.organizationId);
  }

  const callParams = await getCallParams("GET");
  // Prefer explicit org filter over header "all"
  if (payload.organizationId && payload.organizationId !== "all") {
    (callParams.headers as any)["x-organization-id"] = payload.organizationId;
  }
  return makeCall(
    `${urls.url_prod}/payment/list?${params.toString()}`,
    callParams
  );
}

/**
 * (Re-)send a payment link by email.
 *
 * Pass `paymentId` for an existing attempt (re-uses the same amount) or
 * `leadId` for an unpaid lead that has no payment row yet. `amount` is only
 * needed when there is nothing to inherit it from.
 */
export async function sendPaymentLink(payload: {
  paymentId?: string;
  leadId?: string;
  email?: string;
  amount?: string | number;
  organizationId?: string;
}) {
  const callParams = await getCallParams("POST", {
    paymentId: payload.paymentId || "",
    leadId: payload.leadId || "",
    email: payload.email || "",
    ...(payload.amount ? { amount: payload.amount } : {}),
  });
  if (payload.organizationId && payload.organizationId !== "all") {
    (callParams.headers as any)["x-organization-id"] = payload.organizationId;
  }
  return makeCall(`${urls.url_prod}/payment/send-link`, callParams);
}

export type BulkSendItem = {
  paymentId?: string;
  leadId?: string;
  email?: string;
  amount?: string | number;
};

/**
 * Bulk (re-)send payment links for a table selection.
 * Each item keeps the amount from its own previous attempt unless `amount`
 * is passed, which then applies to every row that has none.
 */
export async function sendPaymentLinksBulk(payload: {
  items: BulkSendItem[];
  amount?: string | number;
  organizationId?: string;
}) {
  const callParams = await getCallParams("POST", {
    items: payload.items,
    ...(payload.amount ? { amount: payload.amount } : {}),
  });
  if (payload.organizationId && payload.organizationId !== "all") {
    (callParams.headers as any)["x-organization-id"] = payload.organizationId;
  }
  return makeCall(
    `${urls.url_prod}/payment/send-links-bulk`,
    callParams,
    // Each row is a gateway call plus an SES send.
    240000
  );
}

export async function syncEasebuzzTransactions() {
  const callParams = await getCallParams("POST");
  return makeCall(
    `${urls.url_prod}/payment/easebuzz/sync-transactions`,
    callParams
  );
}
